import { useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { FileText, Loader2, Plus, Search, Trash2, UserMinus, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ColumnCells, ResizableTableHead, RowActions } from '@/components/table'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import type { SortAccessors } from '@/lib/tableSort'
import { cn } from '@/lib/utils'
import { resourceProfilesApi, type AssignmentGroup, type ResourceSummary } from '@/api/resourceCenterApi'
import {
  useAssignmentGroups,
  useGroupFormOptions,
  useInvalidateResourceProfiles,
  useResources,
} from '@/hooks/useResourceCenter'
import { sameSet, setPrimary, toggleMembership } from '@/lib/assignmentGroups'
import { CreateGroupDialog } from './CreateGroupDialog'
import { FormLinkPicker } from './FormLinkPicker'

/**
 * Resources → Groups tab (z8nrz7f0vw, deck slides 3, 5 and 6). A group is a
 * team the center names itself — Micro Team, Chemistry-2 Team — linked to the
 * forms its members work on. New groups are made in a modal; each group then
 * has two sides: its Forms (picked by procedure, form type and search) and its
 * People (members of this center, one of whose groups is their Primary).
 */
export function GroupsTab({ canManage }: { canManage: boolean }) {
  const groups = useAssignmentGroups()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [search, setSearch] = useState('')
  const [error, setError] = useState<string | null>(null)

  const list = groups.data ?? []
  const needle = search.trim().toLowerCase()
  const shown = needle ? list.filter(g => g.name.toLowerCase().includes(needle)) : list
  const selected = list.find(g => g.id === selectedId) ?? list[0] ?? null

  return (
    <div className='space-y-3'>
      {error && <p className='rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive'>{error}</p>}
      {groups.error && <p className='text-sm text-destructive'>{groups.error.message}</p>}

      <div className='grid gap-4 lg:grid-cols-[18rem_1fr]'>
        <aside className='flex flex-col gap-3 rounded-2xl border bg-background p-3 shadow-sm lg:sticky lg:top-4 lg:self-start'>
          <div className='flex items-center justify-between px-1'>
            <h2 className='text-sm font-semibold'>
              Groups <span className='font-normal text-muted-foreground'>({list.length})</span>
            </h2>
            {canManage && (
              <Button size='sm' className='h-8 rounded-lg' onClick={() => setCreating(true)}>
                <Plus className='mr-1 h-4 w-4' /> New group
              </Button>
            )}
          </div>
          {list.length > 5 && (
            <div className='relative'>
              <Search className='pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground' />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder='Find a group'
                aria-label='Find a group'
                className='h-9 rounded-lg pl-8 text-sm'
              />
            </div>
          )}
          {groups.isLoading && <p className='px-1 text-sm text-muted-foreground'>Loading…</p>}
          {groups.isSuccess && list.length === 0 && (
            <div className='rounded-xl border border-dashed px-4 py-8 text-center'>
              <p className='text-sm font-medium'>No groups yet</p>
              <p className='mt-1 text-xs text-muted-foreground'>
                A group is a team with the forms it works on — Micro Team, Chemistry-2 Team…
              </p>
            </div>
          )}
          <nav className='space-y-1'>
            {shown.map(g => {
              const active = selected?.id === g.id
              return (
                <button
                  key={g.id}
                  type='button'
                  onClick={() => setSelectedId(g.id)}
                  aria-current={active ? 'true' : undefined}
                  className={cn(
                    'group flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-all',
                    active
                      ? 'border-primary/40 bg-primary/[0.06] shadow-sm'
                      : 'border-transparent hover:border-border hover:bg-muted/50',
                  )}
                >
                  <span
                    className={cn(
                      'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-xs font-bold',
                      active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
                    )}
                  >
                    {g.name
                      .split(/\s+/)
                      .slice(0, 2)
                      .map(w => w[0]?.toUpperCase())
                      .join('')}
                  </span>
                  <span className='min-w-0 flex-1'>
                    <span className='block truncate text-sm font-medium'>{g.name}</span>
                    <span className='flex gap-3 text-xs text-muted-foreground'>
                      <span className='flex items-center gap-1'>
                        <FileText className='h-3 w-3' /> {g.forms.length} forms
                      </span>
                      <span className='flex items-center gap-1'>
                        <Users className='h-3 w-3' /> {g.members.length}
                      </span>
                    </span>
                  </span>
                </button>
              )
            })}
          </nav>
        </aside>

        <section className='min-w-0'>
          {selected ? (
            <GroupDetail
              key={selected.id}
              group={selected}
              canManage={canManage}
              onError={setError}
              onDeleted={() => setSelectedId(null)}
            />
          ) : (
            <div className='flex flex-col items-center gap-3 rounded-2xl border border-dashed bg-background px-6 py-20 text-center'>
              <span className='flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary'>
                <Users className='h-5 w-5' />
              </span>
              <p className='text-sm font-medium'>Create your first group</p>
              <p className='max-w-sm text-sm text-muted-foreground'>
                Link a team to the forms it works on, then add the people in it.
              </p>
              {canManage && (
                <Button onClick={() => setCreating(true)}>
                  <Plus className='mr-1 h-4 w-4' /> New group
                </Button>
              )}
            </div>
          )}
        </section>
      </div>

      <CreateGroupDialog open={creating} onOpenChange={setCreating} onCreated={g => setSelectedId(g.id)} />
    </div>
  )
}

function GroupDetail({
  group,
  canManage,
  onError,
  onDeleted,
}: {
  group: AssignmentGroup
  canManage: boolean
  onError: (message: string | null) => void
  onDeleted: () => void
}) {
  const invalidate = useInvalidateResourceProfiles()
  const [name, setName] = useState(group.name)
  const [description, setDescription] = useState(group.description ?? '')
  const fail = (e: unknown) => onError(e instanceof Error ? e.message : 'Something went wrong.')

  const saveDetails = useMutation({
    mutationFn: () =>
      resourceProfilesApi.updateGroup(group.id, { name: name.trim(), description: description.trim() || null }),
    onSuccess: () => {
      onError(null)
      void invalidate()
    },
    onError: fail,
  })
  const remove = useMutation({
    mutationFn: () => resourceProfilesApi.deleteGroup(group.id),
    onSuccess: () => {
      onError(null)
      onDeleted()
      void invalidate()
    },
    onError: fail,
  })
  const detailsDirty = name.trim() !== group.name || (description.trim() || null) !== group.description
  const primaryCount = group.members.filter(m => m.isPrimary).length

  return (
    <div className='space-y-4'>
      <div className='rounded-2xl border bg-background p-5 shadow-sm'>
        <div className='flex flex-wrap items-start gap-4'>
          <div className='min-w-0 flex-1 space-y-2'>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              readOnly={!canManage}
              aria-label='Group name'
              className='w-full rounded-md border border-transparent bg-transparent px-1 text-xl font-semibold outline-none transition-colors hover:border-input focus:border-input read-only:hover:border-transparent'
            />
            <input
              value={description}
              onChange={e => setDescription(e.target.value)}
              readOnly={!canManage}
              aria-label='Description'
              placeholder={canManage ? 'Add a description' : ''}
              className='w-full rounded-md border border-transparent bg-transparent px-1 text-sm text-muted-foreground outline-none transition-colors hover:border-input focus:border-input read-only:hover:border-transparent'
            />
          </div>
          <div className='flex items-center gap-2'>
            {detailsDirty && canManage && (
              <>
                <Button
                  size='sm'
                  variant='ghost'
                  onClick={() => {
                    setName(group.name)
                    setDescription(group.description ?? '')
                  }}
                >
                  Undo
                </Button>
                <Button size='sm' disabled={!name.trim() || saveDetails.isPending} onClick={() => saveDetails.mutate()}>
                  Save
                </Button>
              </>
            )}
            {canManage && (
              <Button
                size='icon'
                variant='ghost'
                className='text-muted-foreground hover:bg-destructive/10 hover:text-destructive'
                disabled={group.members.length > 0 || remove.isPending}
                title={group.members.length > 0 ? 'Remove everyone from the group before deleting it' : 'Delete group'}
                aria-label='Delete group'
                onClick={() => remove.mutate()}
              >
                <Trash2 className='h-4 w-4' />
              </Button>
            )}
          </div>
        </div>
        <dl className='mt-4 grid grid-cols-3 gap-3 sm:max-w-md'>
          {(
            [
              ['Forms', group.forms.length],
              ['People', group.members.length],
              ['Have it as their Team', primaryCount],
            ] as const
          ).map(([label, n]) => (
            <div key={label} className='rounded-xl bg-muted/50 px-3 py-2'>
              <dd className='text-lg font-semibold tabular-nums'>{n}</dd>
              <dt className='text-xs text-muted-foreground'>{label}</dt>
            </div>
          ))}
        </dl>
      </div>

      <Tabs defaultValue='forms'>
        <TabsList>
          <TabsTrigger value='forms'>Forms ({group.forms.length})</TabsTrigger>
          <TabsTrigger value='people'>People ({group.members.length})</TabsTrigger>
        </TabsList>
        <TabsContent value='forms'>
          <GroupForms group={group} canManage={canManage} onError={onError} />
        </TabsContent>
        <TabsContent value='people'>
          <GroupPeople group={group} canManage={canManage} onError={onError} />
        </TabsContent>
      </Tabs>
    </div>
  )
}

/** The group's forms: the shared picker, a draft until Save. */
function GroupForms({
  group,
  canManage,
  onError,
}: {
  group: AssignmentGroup
  canManage: boolean
  onError: (message: string | null) => void
}) {
  const invalidate = useInvalidateResourceProfiles()
  const options = useGroupFormOptions(true)
  const savedForms = useMemo(() => group.forms.map(f => f.familyId), [group.forms])
  const [linked, setLinked] = useState<string[]>(savedForms)
  const dirty = !sameSet(linked, savedForms)

  const save = useMutation({
    mutationFn: () => resourceProfilesApi.setGroupForms(group.id, linked),
    onSuccess: () => {
      onError(null)
      void invalidate()
    },
    onError: e => onError(e instanceof Error ? e.message : 'Something went wrong.'),
  })

  // Forms the group holds that no longer have an active version.
  const known = new Set((options.data?.forms ?? []).map(f => f.familyId))
  const retired = group.forms.filter(f => options.isSuccess && !known.has(f.familyId))

  return (
    <div className='space-y-3 rounded-2xl border bg-background p-4 shadow-sm'>
      {options.error && <p className='text-sm text-destructive'>{options.error.message}</p>}
      {dirty && canManage && (
        <div className='flex items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2'>
          <p className='text-sm text-amber-800'>
            Unsaved changes — {linked.length} form{linked.length === 1 ? '' : 's'} selected.
          </p>
          <div className='flex gap-1.5'>
            <Button size='sm' variant='ghost' className='h-8' onClick={() => setLinked(savedForms)}>
              Undo
            </Button>
            <Button size='sm' className='h-8' disabled={save.isPending} onClick={() => save.mutate()}>
              {save.isPending && <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />}
              Save forms
            </Button>
          </div>
        </div>
      )}
      <FormLinkPicker
        options={options.data}
        loading={options.isLoading}
        linked={linked}
        saved={savedForms}
        onChange={setLinked}
        canManage={canManage}
        initialShow={savedForms.length > 0 ? 'linked' : 'all'}
      />
      {retired.length > 0 && (
        <p className='text-xs text-muted-foreground'>
          Also linked, no longer active: {retired.map(f => f.name ?? f.familyId).join(', ')}. Saving keeps them unless
          you remove them.
        </p>
      )}
    </div>
  )
}

function FilterField({
  label,
  className,
  children,
}: {
  label: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={cn('space-y-1', className)}>
      <span className='block text-xs font-medium text-muted-foreground'>{label}</span>
      {children}
    </div>
  )
}

// ─── People ──────────────────────────────────────────────────────────────────

const PEOPLE_TABLE_ID = 'resource-group-people'
const PEOPLE_COLUMNS: TableColumnDef[] = [
  { id: 'name', header: 'Name', locked: true, defaultWidth: 220, sortable: true },
  { id: 'email', header: 'Email', defaultWidth: 240, sortable: true },
  { id: 'role', header: 'In this group', defaultWidth: 150, sortable: true },
  { id: 'team', header: 'Team (Primary)', defaultWidth: 180, sortable: true },
  { id: 'supervisor', header: 'Supervisor', defaultWidth: 180, sortable: true },
  { id: 'actions', header: 'Actions', locked: true, defaultWidth: 90 },
]

/**
 * Who is in the group. People are this center's real members (the same list as
 * the People tab) — adding one here adds the group to their profile.
 */
function GroupPeople({
  group,
  canManage,
  onError,
}: {
  group: AssignmentGroup
  canManage: boolean
  onError: (message: string | null) => void
}) {
  const resources = useResources()
  const invalidate = useInvalidateResourceProfiles()
  const [picked, setPicked] = useState('')
  const everyone = resources.data ?? []
  const members = everyone.filter(r => r.groups.some(g => g.id === group.id))
  const others = everyone.filter(r => r.enabled && !r.groups.some(g => g.id === group.id))

  const write = useMutation({
    mutationFn: ({ person, next }: { person: ResourceSummary; next: ReturnType<typeof toggleMembership> }) =>
      resourceProfilesApi.setGroups(person.userId, next),
    onSuccess: () => {
      setPicked('')
      onError(null)
      void invalidate()
    },
    onError: e => onError(e instanceof Error ? e.message : 'Something went wrong.'),
  })
  const payload = (p: ResourceSummary) => p.groups.map(g => ({ groupId: g.id, isPrimary: g.isPrimary }))
  const roleIn = (p: ResourceSummary) => p.groups.find(g => g.id === group.id)?.isPrimary ?? false

  const sortAccessors = useMemo<SortAccessors<ResourceSummary>>(
    () => ({
      name: p => p.name,
      email: p => p.email,
      role: p => (p.groups.find(g => g.id === group.id)?.isPrimary ? 0 : 1),
      team: p => p.teams[0]?.name ?? null,
      supervisor: p => p.supervisor?.name ?? null,
    }),
    [group.id],
  )
  const cols = useTableColumns(PEOPLE_TABLE_ID, canManage ? PEOPLE_COLUMNS : PEOPLE_COLUMNS.filter(c => c.id !== 'actions'))
  const rows = useSortedRows(members, cols.sort, sortAccessors)

  return (
    <div className='space-y-3 rounded-2xl border bg-background p-4 shadow-sm'>
      {resources.error && <p className='text-sm text-destructive'>{resources.error.message}</p>}
      {canManage && (
        <div className='flex flex-wrap items-end gap-2'>
          <FilterField label='Add a person from this center' className='w-80'>
            <SearchableSelect
              buttonClassName='h-8 text-sm'
              value={picked}
              onChange={setPicked}
              options={others.map(p => ({ value: p.userId, label: p.email ? `${p.name} · ${p.email}` : p.name }))}
              placeholder={resources.isLoading ? 'Loading people…' : 'Choose a person'}
              emptyMessage='Everyone is already in this group.'
            />
          </FilterField>
          <Button
            size='sm'
            className='h-8'
            disabled={!picked || write.isPending}
            onClick={() => {
              const person = everyone.find(p => p.userId === picked)
              if (person) write.mutate({ person, next: toggleMembership(payload(person), group.id, true) })
            }}
          >
            <Plus className='mr-1 h-3.5 w-3.5' /> Add to group
          </Button>
          <p className='pb-1.5 text-xs text-muted-foreground'>
            A person's first group becomes their Primary (their Team).
          </p>
        </div>
      )}

      {resources.isSuccess && rows.length === 0 ? (
        <div className='py-12 text-center text-sm text-muted-foreground'>Nobody is in this group yet.</div>
      ) : (
        <div className='overflow-hidden rounded-lg border'>
          <Table>
            <TableHeader>
              <TableRow>
                {cols.visible.map(({ def }) => (
                  <ResizableTableHead
                    key={def.id}
                    columnId={def.id}
                    columns={cols}
                    label={def.header}
                    className={def.id === 'actions' ? 'text-right' : undefined}
                    resizable={def.id !== 'actions'}
                  >
                    {def.header}
                  </ResizableTableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(p => {
                const primary = roleIn(p)
                return (
                  <TableRow key={p.userId} className={cn(!p.enabled && 'opacity-60')}>
                    <ColumnCells
                      columns={cols}
                      cells={{
                        name: <TableCell className='font-medium'>{p.name}</TableCell>,
                        email: <TableCell className='text-muted-foreground'>{p.email || '—'}</TableCell>,
                        role: (
                          <TableCell>
                            <button
                              type='button'
                              disabled={!canManage || primary || write.isPending}
                              onClick={() => write.mutate({ person: p, next: setPrimary(payload(p), group.id) })}
                              title={primary ? 'This is their Team' : 'Make this their Primary group'}
                              className={cn(
                                'rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors',
                                primary
                                  ? 'bg-primary text-primary-foreground'
                                  : 'border text-muted-foreground enabled:hover:border-primary/50 enabled:hover:text-foreground',
                              )}
                            >
                              {primary ? 'Primary' : 'Secondary'}
                            </button>
                          </TableCell>
                        ),
                        team: <TableCell>{p.teams.map(t => t.name).join(', ') || '—'}</TableCell>,
                        supervisor: <TableCell className='text-muted-foreground'>{p.supervisor?.name ?? '—'}</TableCell>,
                        actions: (
                          <TableCell>
                            <RowActions>
                              <Button
                                variant='ghost'
                                size='icon'
                                title='Remove from group'
                                aria-label={`Remove ${p.name} from the group`}
                                disabled={write.isPending}
                                className='text-muted-foreground hover:bg-destructive/10 hover:text-destructive'
                                onClick={() =>
                                  write.mutate({ person: p, next: toggleMembership(payload(p), group.id, false) })
                                }
                              >
                                <UserMinus className='h-4 w-4' />
                              </Button>
                            </RowActions>
                          </TableCell>
                        ),
                      }}
                    />
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
